import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ConfirmationModal from '../components/ConfirmationModal';
import toast from 'react-hot-toast';
import '../styles/UpdateStudent.css';

const UpdateStudent = () => {
    const [student, setStudent] = useState({
        first_name: '',
        last_name: '',
        email: '',
        grade: '',
        list_of_trained_tools: [],
        is_admin: false,
        profile_image: '',
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [toolToRemove, setToolToRemove] = useState(null);
    const { id } = useParams();
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchStudent = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/students/${id}`, {});
                setStudent(response.data);
            } catch (err) {
                setError(err.response.data.message);
            }
        };
        fetchStudent();
    }, [id, API_URL]);

    useEffect(() => {
        const fetchTools = async () => {
            if (searchTerm) {
                try {
                    const response = await axios.get(`${API_URL}/api/tools?search=${searchTerm}`, {});
                    setSearchResults(response.data);
                } catch (err) {
                    setError(err.response.data.message);
                }
            } else {
                setSearchResults([]);
            }
        };
        fetchTools();
    }, [searchTerm, API_URL]);

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        if (e.target.name === 'list_of_trained_tools') {
            setStudent({...student, [e.target.name]: value.split(',')});
        } else {
            setStudent({...student, [e.target.name]: value});
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields
        let isValid = true;
        let errorMessage = '';

        // Validate user_id if provided
        if (student.user_id && !/^\d{6}$/.test(student.user_id)) {
            isValid = false;
            errorMessage = 'User ID must be a six digit number';
        }
        // Validate first_name
        else if (!/^[A-Za-z() ]+$/.test(student.first_name)) {
            isValid = false;
            errorMessage = 'First name must contain only letters, spaces, and brackets';
        }
        // Validate last_name
        else if (!/^[A-Za-z() ]+$/.test(student.last_name)) {
            isValid = false;
            errorMessage = 'Last name must contain only letters, spaces, and brackets';
        }
        // Validate grade
        else {
            const gradeNum = parseInt(student.grade);
            if (isNaN(gradeNum) || gradeNum < 6 || gradeNum > 12) {
                isValid = false;
                errorMessage = 'Grade must be a number between 6 and 12';
            }
        }

        if (!isValid) {
            toast.error(errorMessage, {
                iconTheme: {
                    primary: '#FFFFFF',
                    secondary: '#E61920',
                },
                style: {
                    backgroundColor: '#E61920',
                    color: '#FFFFFF',
                },
            });
            return;
        }

        try {
            const response = await axios.put(`${API_URL}/api/students/${id}`, {
                first_name: student.first_name,
                last_name: student.last_name,
                email: student.email,
                grade: student.grade,
                list_of_trained_tools: student.list_of_trained_tools,
                is_admin: student.is_admin,
                profile_image: student.profile_image,
            }, {});
            
            if (response.data) {
                navigate(`/student/${id}`);
                toast.success('Student updated successfully!', {
                    iconTheme: {
                        primary: '#333',
                        secondary: '#DCB41F',
                    },
                    style: {
                        backgroundColor: '#333',
                        color: '#DCB41F',
                    },
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Error updating student');
            toast.error('Error updating student', {
                iconTheme: {
                    primary: '#FFFFFF',
                    secondary: '#E61920',
                },
                style: {
                    backgroundColor: '#E61920',
                    color: '#FFFFFF',
                },
            });
        }
    };

    const handleCancel = () => {
        navigate('/edit-students');
        toast('No changes were made.', {
            iconTheme: {
                primary: '#333',
                secondary: '#DCB41F',
            },
            style: {
                backgroundColor: '#333',
                color: '#DCB41F',
            },
        });
    };

    const handleAddTool = (tool) => {
        const alreadyAdded = student.list_of_trained_tools.some(t => t.tool_id === tool.tool_id);
        if (alreadyAdded) {
            toast.error('Tool has already been added.', {
                iconTheme: {
                    primary: '#FFFFFF',
                    secondary: '#E61920',
                },
                style: {
                    backgroundColor: '#E61920',
                    color: '#FFFFFF',
                },
            });
        } else {
            setStudent(prevState => ({
                ...prevState,
                list_of_trained_tools: [...prevState.list_of_trained_tools, {
                    tool_id: tool.tool_id,
                    tool_name: tool.tool_name,
                    tool_model: tool.tool_model,
                    tool_category: tool.tool_category,
                    restriction: tool.restriction,
                }]
            }));
        }
    };

    const handleRemoveTool = (tool) => {
        setToolToRemove(tool);
        setIsModalOpen(true);
    };

    const confirmRemoveTool = () => {
        setStudent(prevState => ({
            ...prevState,
            list_of_trained_tools: prevState.list_of_trained_tools.filter(t => t.tool_id !== toolToRemove.tool_id)
        }));
        setIsModalOpen(false);
        setToolToRemove(null);
        toast.success('Tool removed successfully.', {
            iconTheme: {
                primary: '#333',
                secondary: '#DCB41F',
            },
            style: {
                backgroundColor: '#333',
                color: '#DCB41F',
            },
        });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setToolToRemove(null);
    };

    return (
        <div>
            <Header />
            <div className="update-student-container">
                <h2>Update Student</h2>
                {error && <p className="error">{error}</p>}
                <form onSubmit={handleSubmit}>
                    <input placeholder="User ID" type="text" name="user_id" value={student.user_id} onChange={handleChange} required />
                    <input placeholder="First Name" type="text" name="first_name" value={student.first_name} onChange={handleChange} required />
                    <input placeholder="Last Name" type="text" name="last_name" value={student.last_name} onChange={handleChange} required />
                    <input placeholder="example@isyedu.org" type="email" name="email" value={student.email} onChange={handleChange} required />
                    <input placeholder="Grade" type="text" name="grade" value={student.grade} onChange={handleChange} required />
                    <input placeholder="Profile Image URL" type="url" name="profile_image" value={student.profile_image} onChange={handleChange}/>
                    <div className="admin-switch">
                        <label>
                            Admin?
                            <input
                                type="checkbox"
                                name="is_admin"
                                checked={student.is_admin}
                                onChange={handleChange}
                            />
                        </label>
                    </div>
                    
                    {/* Only show tool management section if student is not an admin */}
                    {!student.is_admin && (
                        <>
                            <div className="trained-tools">
                                <h3>Tools Trained:</h3>
                                {student.list_of_trained_tools.map(tool => (
                                    <div key={tool.tool_id} className="trained-tool-item">
                                        <span>{tool.tool_name}</span>
                                        <span>{tool.tool_model}</span>
                                        <span>{tool.tool_category}</span>
                                        <button type="button" className="remove-button" onClick={() => handleRemoveTool(tool)}>Remove</button>
                                    </div>
                                ))}
                            </div>
                            <div className="tool-search">
                                <input
                                    type="text"
                                    placeholder="Search for tools..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <div className="tool-results">
                                    {searchResults.map(tool => (
                                        <div key={tool.tool_id} className="tool-item">
                                            <span>{tool.tool_name}</span>
                                            <span>{tool.tool_model}</span>
                                            <span>{tool.tool_category}</span>
                                            <button type="button" className="add-button" onClick={() => handleAddTool(tool)}>Add</button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    <div className="button-group">
                        <button type="submit">Update Student</button>
                        <button className="cancel-button" type="button" onClick={handleCancel}>Cancel</button>
                    </div>
                </form>
            </div>
            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={closeModal}
                onConfirm={confirmRemoveTool}
                message="Are you sure you want to remove this tool?"
                confirmText="Confirm"
                cancelText="Cancel"
            />
        </div>
    );
};

export default UpdateStudent;
